import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BatchReviewModal = ({
  isOpen,
  onClose,
  stagedIds = [],
  orgId,
  userId,
  onComplete,
  onRemoveItem
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!isOpen) return;
      setError('');
      setRows([]);
      if (!orgId || stagedIds.length === 0) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('epos_price_history')
          .select('id, sku, old_price, new_price, changed_at, invoice_id, epos_products(name), parsed_invoices(vendor_name, invoice_number, invoice_date)')
          .eq('org_id', orgId)
          .in('id', stagedIds);
        if (error) throw error;
        setRows(data || []);
      } catch (e) {
        setError(e?.message || 'Failed to load staged items');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, orgId, stagedIds]);

  const totals = useMemo(() => {
    return rows.reduce((acc, row) => {
      const oldP = toNum(row?.old_price) || 0;
      const newP = toNum(row?.new_price) || 0;
      acc.old += oldP;
      acc.new += newP;
      acc.variance += (newP - oldP);
      return acc;
    }, { old: 0, new: 0, variance: 0 });
  }, [rows]);

  const handleRemove = (id) => {
    if (!id) return;
    onRemoveItem?.(id);
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleDownloadCsv = (exportDateIso) => {
    const headers = ['SKU', 'Product Name', 'Old Cost', 'New Cost', 'Variance', 'Vendor', 'Export Date'];
    const csvRows = [headers.join(',')];
    const exportDate = formatCsvDate(exportDateIso);

    rows.forEach((row) => {
      const oldP = toNum(row?.old_price);
      const newP = toNum(row?.new_price);
      const variance = (oldP != null && newP != null) ? (newP - oldP) : null;
      csvRows.push([
        csvEscape(row?.sku),
        csvEscape(row?.epos_products?.name || ''),
        formatCsvNumber(oldP),
        formatCsvNumber(newP),
        formatCsvNumber(variance),
        csvEscape(row?.parsed_invoices?.vendor_name || ''),
        csvEscape(exportDate)
      ].join(','));
    });

    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `epos_price_updates_${exportDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCommit = async () => {
    if (!orgId || !userId || rows.length === 0) return;
    const exportIds = rows.map((row) => row.id);
    setSaving(true);
    setError('');
    try {
      const { data: exportRow, error: exportErr } = await supabase
        .from('exports')
        .insert({
          org_id: orgId,
          user_id: userId,
          format: 'csv',
          count: exportIds.length,
          type: 'epos_update'
        })
        .select('id, created_at')
        .maybeSingle();
      if (exportErr) throw exportErr;

      const exportId = exportRow?.id;
      const exportDate = exportRow?.created_at || new Date().toISOString();
      if (exportId) {
        const { error: updateErr } = await supabase
          .from('epos_price_history')
          .update({ export_id: exportId })
          .eq('org_id', orgId)
          .in('id', exportIds);
        if (updateErr) throw updateErr;
      }

      handleDownloadCsv(exportDate);
      onComplete?.();
    } catch (e) {
      setError(e?.message || 'Failed to commit batch');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="ClipboardList" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Batch Review</h2>
              <p className="text-xs text-muted-foreground">Confirm the staged price changes before exporting.</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-160px)]">
          {error && <div className="text-sm text-destructive mb-2">{error}</div>}
          {loading ? (
            <div className="text-sm">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No staged items to review.</div>
          ) : (
            <div className="overflow-auto border border-border rounded-md">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-muted text-muted-foreground">
                    <Th>Vendor</Th>
                    <Th>SKU</Th>
                    <Th>Product</Th>
                    <Th className="text-right">Old Cost</Th>
                    <Th className="text-right">New Cost</Th>
                    <Th className="text-right">Variance</Th>
                    <Th>Invoice</Th>
                    <Th className="text-right">Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const oldP = toNum(row?.old_price);
                    const newP = toNum(row?.new_price);
                    const variance = (oldP != null && newP != null) ? (newP - oldP) : null;
                    return (
                      <tr key={row.id} className="border-t border-border">
                        <Td>{row?.parsed_invoices?.vendor_name || 'Unknown'}</Td>
                        <Td mono>{row?.sku || ''}</Td>
                        <Td>{row?.epos_products?.name || ''}</Td>
                        <Td num>{fmtMoney(oldP)}</Td>
                        <Td num>{fmtMoney(newP)}</Td>
                        <Td num>
                          {variance != null ? (
                            <span className={variance >= 0 ? 'text-success' : 'text-destructive'}>
                              {variance > 0 ? '+' : ''}{fmtMoney(variance)}
                            </span>
                          ) : ''}
                        </Td>
                        <Td>
                          {row?.parsed_invoices?.invoice_number || row?.invoice_id || ''}
                        </Td>
                        <Td num>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(row.id)}
                            iconName="Trash2"
                            className="text-destructive"
                          />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/40">
                    <Td colSpan={3} className="font-medium">Totals</Td>
                    <Td num className="font-medium">{fmtMoney(totals.old)}</Td>
                    <Td num className="font-medium">{fmtMoney(totals.new)}</Td>
                    <Td num className="font-medium">{fmtMoney(totals.variance)}</Td>
                    <Td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {rows.length} item{rows.length === 1 ? '' : 's'} staged
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button variant="default" onClick={handleCommit} disabled={rows.length === 0} loading={saving}>
              Commit &amp; Download CSV
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document?.body) {
    return createPortal(modal, document.body);
  }
  return modal;
};

const Th = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 border-b border-border sticky top-0 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, num = false, className = '' }) => (
  <td className={`px-3 py-2 align-top ${mono ? 'font-mono text-[11px]' : ''} ${num ? 'text-right' : ''} ${className}`}>{children}</td>
);

function toNum(v) { if (v === undefined || v === null || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
const currencyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
function fmtMoney(v) { if (v == null) return ''; const n = Number(v); if (!Number.isFinite(n)) return ''; return currencyFormatter.format(n); }
function formatCsvNumber(v) { if (v == null || Number.isNaN(Number(v))) return ''; return Number(v).toFixed(2); }
function formatCsvDate(iso) { try { const d = new Date(iso); if (Number.isNaN(d.getTime())) return ''; return d.toISOString().slice(0, 10); } catch { return ''; } }
function csvEscape(value) { const s = value == null ? '' : String(value); return `"${s.replace(/"/g, '""')}"`; }

export default BatchReviewModal;
